import clsx from 'clsx';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMedia } from 'react-use';
import { getSpecialHoursArray } from '@srcDS/hooks/useStoreLocator';
import { handleize } from '@srcDS/utils/helpers';
import Button from '../../atoms/button';
import { Department } from '../../atoms/icons';
import CarretArrowLeft from '../../atoms/icons/CarretArrowLeft';
import ClockStroke from '../../atoms/icons/ClockStroke';
import LocationStoreLocatorStroke from '../../atoms/icons/LocationStoreLocatorStroke';
import PhoneStroke from '../../atoms/icons/PhoneStroke';
import { StoreSchema } from '../../molecules/storeSchema/StoreSchema';
import { Map } from '../map/Map';
import { MARKER_ICON, mapStyles } from '../map/utils';
import { titleTheme } from './options';
import styles from './StoreDetail.module.scss';
import { Context, SpecialHour, StoreDetailProps } from '@srcDS/components/organisms/storeLocator/types';


const MOCK_DESTINATIONS_DATA = [
  {
    id: 1,
    title: 'Falun',
    coordinates: { lat: 60.610361157011646, lng: 15.63610136902125 },
    src: 'https://i.pinimg.com/originals/09/9e/60/099e600bcfa057bf1c9ecdcce0ad529c.jpg',
  },
];


const StoreDetail: React.FC<StoreDetailProps> = ({ chosenStore, showCanonicalLink }) => {
  const context = useSelector<{ app: { context: Context } }, Context>((state) => state.app.context);
  const isMobile = useMedia('(max-width: 680px)', false);
  const history = useHistory();

  const {
    storeName,
    storeDescription,
    address1,
    address2,
    city,
    state,
    postcode,
    telephone,
    coordinates,
    collect,
    key,
  } = chosenStore;

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const schedules: { day: string; time: string }[] = [];

  days.forEach((el) => {
    if (!chosenStore[`hours_${el.slice(0, 3)}_open`] && !chosenStore[`hours_${el.slice(0, 3)}_open`]) return;

    schedules.push({
      day: el.slice(0, 1).toUpperCase() + el.slice(1),
      time: `${chosenStore[`hours_${el.slice(0, 3)}_open`]}-${chosenStore[`hours_${el.slice(0, 3)}_close`]}`,
    });
  });

  const storeSchema = useMemo(() => <StoreSchema store={chosenStore} />, [chosenStore]);

  const specialHours: SpecialHour[] = getSpecialHoursArray(chosenStore);

  const getCanonicalLink = (): string => {
    const store = storeName && handleize(storeName);

    if (key && state && store && city) {
      return `https://${context.host}/store-locator/store-details?state=${state}&suburb=${handleize(city)}&id=${key}`;
    } else {
      return `https://${context.host}/store-locator/store-details?id=${key}`;
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${storeName} in ${state} | ${titleTheme}`}</title>
        <meta
          name="description"
          content={`Discover store opening hours and more for ${storeName} in ${state} and visit in person today!`}
        />
        {showCanonicalLink && <link rel="canonical" href={getCanonicalLink()} />}
      </Helmet>

      {storeSchema}

      <div className={styles.wrapper}>
        <div className={styles.detailWrapper}>
          <h1 className={styles.detailTitle}>{storeName}</h1>
          <p className={styles.detailText}>{storeDescription}</p>
          <div className={clsx(styles.flexBlock, styles.detailAddress)}>
            <LocationStoreLocatorStroke className={styles.detailIcon} width={26} height={26} />
            <span>
              <span>{address1}</span>
              <span>{`${address2 ? `${address2}, ` : ''}${city} ${state} ${postcode}`}</span>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`}
                target="_blank"
                className={clsx(styles.detailText, styles.addressLink)}
              >
                Get Directions
              </a>
              {collect && <div className={styles.clickAndCollectText}>Click & Collect is available</div>}
            </span>
          </div>
          <div className={clsx(styles.flexBlock, styles.phoneBlock)}>
            <PhoneStroke className={styles.detailIcon} width={26} height={26} />
            <p>Phone: </p>
            {isMobile ? (
              <a className={clsx(styles.detailText, styles.detailLink)} href={`tel:${telephone.replace(/\s/g, '')}`}>
                {telephone}
              </a>
            ) : (
              <p className={clsx(styles.detailText, styles.detailLink)}>{telephone}</p>
            )}
          </div>
          <div className={clsx(styles.flexBlock, styles.schedulesBlock)}>
            {!!schedules.length && <ClockStroke className={styles.detailIcon} />}
            <div>
              {schedules?.map((schedule, index) => (
                <div key={schedule.day + index} className={clsx(styles.flexBlock, styles.scheduleItem)}>
                  <p>{schedule.day}:</p>
                  <p>{schedule.time}</p>
                </div>
              ))}
              {specialHours?.length
                ? specialHours.map((item, index) => (
                    <div key={index} className={clsx(styles.flexBlock, styles.scheduleItem, styles.fullWidth)}>
                      <p>{item.name}:</p>
                      <p>{item.value}</p>
                    </div>
                  ))
                : null}
            </div>
          </div>

          {chosenStore?.['department-list']?.length > 0 && (
            <div className={clsx(styles.flexBlock, styles.departmentsBlock)}>
              <Department className={styles.detailIcon} />
              <div>
                <p>Department:</p>
                <ul>
                  {chosenStore?.['department-list']?.map((item, key) => (
                    <li key={key}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {!isMobile && (
            /*@ts-ignore*/
            <Button
              className={styles.detailGoBackBtn}
              variant="primaryOutlined"
              as="button"
              onClick={() => {
                history && history.push(`/store-locator`);
              }}
            >
              <CarretArrowLeft width={22} height={22} />
              Back to all stores
            </Button>
          )}
        </div>
        <div className={styles.storeMapContainer}>
          <Map
            chosenOrigin={chosenStore}
            origins={[chosenStore]}
            destinations={MOCK_DESTINATIONS_DATA}
            markerIcon={MARKER_ICON}
            mapStyles={mapStyles}
            customZoom={18}
          />
        </div>
        {isMobile && (
          /*@ts-ignore*/
          <Button
            className={styles.detailGoBackBtn}
            variant="secondary"
            as="button"
            onClick={() => history && history.push(`/store-locator`)}
          >
            <CarretArrowLeft width={22} height={22} className={styles.buttonIcon} />
            Back to all stores
          </Button>
        )}
      </div>
    </>
  );
};

export default StoreDetail;
